import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { getApiBase, getAuthHeaders } from '../../utils/api';
import { AlertTriangle, X, Check } from 'lucide-react';

// Error categories matching backend enum
const ERROR_CATEGORIES = [
  { value: 'date_time', label: 'Date/Time Issue', description: 'Wrong date, time, or timezone' },
  { value: 'location', label: 'Location Issue', description: 'Wrong airport, city, or address' },
  { value: 'flight_details', label: 'Flight Details', description: 'Wrong flight number, airline, terminal' },
  { value: 'hotel_details', label: 'Hotel Details', description: 'Wrong hotel name, room type, confirmation' },
  { value: 'amounts', label: 'Amounts/Pricing', description: 'Wrong price, currency, or totals' },
  { value: 'contact_info', label: 'Contact Info', description: 'Wrong phone, email, or name' },
  { value: 'missing_data', label: 'Missing Data', description: 'Required field not extracted' },
  { value: 'extra_data', label: 'Extra/Wrong Data', description: 'Hallucinated or incorrect values' },
  { value: 'format', label: 'Format Issue', description: 'Correct data but wrong format' },
  { value: 'other', label: 'Other', description: 'Issue not listed above' }
];

// Field options based on component type
const FIELD_OPTIONS = {
  commercial_flight: [
    'airline', 'flight_number', 'departure_airport', 'arrival_airport',
    'departure_date', 'departure_time', 'arrival_date', 'arrival_time',
    'confirmation_number', 'seat', 'class_type', 'passenger_name'
  ],
  private_flight: [
    'operator', 'tail_number', 'aircraft_type', 'departure_airport', 'arrival_airport',
    'departure_date', 'departure_time', 'arrival_date', 'arrival_time',
    'fbo_name', 'passenger_name'
  ],
  hotel: [
    'hotel_name', 'address', 'check_in_date', 'check_in_time',
    'check_out_date', 'check_out_time', 'confirmation_number',
    'room_type', 'guest_name'
  ],
  rental_car: [
    'rental_company', 'pickup_location', 'dropoff_location',
    'pickup_date', 'pickup_time', 'dropoff_date', 'dropoff_time',
    'confirmation_number', 'car_type', 'driver_name'
  ],
  restaurant: [
    'restaurant_name', 'address', 'reservation_date', 'reservation_time',
    'party_size', 'confirmation_number'
  ],
  default: [
    'date', 'time', 'location', 'confirmation_number', 'description', 'other'
  ]
};

const PRIORITY_OPTIONS = [
  { value: 'low', label: 'Low', color: 'bg-ui-hover text-ui-ink-2' },
  { value: 'medium', label: 'Medium', color: 'bg-ui-amber-bg text-ui-amber-fg' },
  { value: 'high', label: 'High', color: 'bg-ui-orange-bg text-ui-orange-fg' },
  { value: 'critical', label: 'Critical', color: 'bg-ui-red-bg text-ui-red-fg' }
];

const ParsingFeedbackModal = ({
  isOpen,
  onClose,
  component,       // The component with parsing issues
  componentType,   // e.g., 'commercial_flight', 'hotel'
  tripId = null,   // Trip ID if component is in a trip
  parsingLogId = null,  // Optional parsing log reference
  documentId = null     // Optional document reference
}) => {
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedFields, setSelectedFields] = useState([]);
  const [description, setDescription] = useState('');
  const [expectedValues, setExpectedValues] = useState('');
  const [priority, setPriority] = useState('medium');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  if (!isOpen) return null;

  const fieldOptions = FIELD_OPTIONS[componentType] || FIELD_OPTIONS.default;

  const toggleCategory = (category) => {
    setSelectedCategories(prev =>
      prev.includes(category)
        ? prev.filter(c => c !== category)
        : [...prev, category]
    );
  };

  const toggleField = (field) => {
    setSelectedFields(prev =>
      prev.includes(field)
        ? prev.filter(f => f !== field)
        : [...prev, field]
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (selectedCategories.length === 0) {
      setError('Please select at least one error type');
      return;
    }

    if (!description.trim()) {
      setError('Please describe the issue');
      return;
    }

    setLoading(true);

    try {
      const apiBase = getApiBase();
      const headers = getAuthHeaders();

      // Parse expected values if provided
      let parsedExpectedValues = null;
      if (expectedValues.trim()) {
        try {
          // Try to parse as JSON first
          parsedExpectedValues = JSON.parse(expectedValues);
        } catch {
          // If not valid JSON, store as a simple object with description
          parsedExpectedValues = { user_provided: expectedValues.trim() };
        }
      }

      const payload = {
        component_id: component.id,
        component_type: componentType,
        trip_id: tripId,
        parsing_log_id: parsingLogId,
        document_id: documentId,
        error_categories: selectedCategories,
        affected_fields: selectedFields,
        user_description: description.trim(),
        expected_values: parsedExpectedValues,
        priority: priority
      };

      // Debug logging for feedback submission
      console.log('[Parsing Feedback] Submitting payload:', {
        component_id: payload.component_id,
        component_type: payload.component_type,
        trip_id: payload.trip_id,
        parsing_log_id: payload.parsing_log_id,
        document_id: payload.document_id
      });

      await axios.post(`${apiBase}/parsing-feedback`, payload, { headers });

      setSuccess(true);
      setTimeout(() => {
        onClose();
        // Reset form
        setSelectedCategories([]);
        setSelectedFields([]);
        setDescription('');
        setExpectedValues('');
        setPriority('medium');
        setSuccess(false);
      }, 1500);

    } catch (err) {
      console.error('Failed to submit feedback:', err);
      setError(err.response?.data?.detail || 'Failed to submit feedback. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return createPortal(
      <div className="fixed inset-0 bg-ui-overlay bg-opacity-50 flex items-center justify-center z-[100]">
        <div className="bg-ui-surface rounded-lg shadow-xl p-8 max-w-md w-full mx-4 text-center">
          <div className="w-16 h-16 bg-ui-green-bg rounded-full flex items-center justify-center mx-auto mb-4">
            <Check className="w-8 h-8 text-ui-green-fg" />
          </div>
          <h3 className="text-xl font-semibold text-ui-ink mb-2">Feedback Submitted</h3>
          <p className="text-ui-ink-3">Thank you for reporting this issue. Our team will review it.</p>
        </div>
      </div>,
      document.body
    );
  }

  return createPortal(
    <div className="fixed inset-0 bg-ui-overlay bg-opacity-50 flex items-center justify-center z-[100] overflow-y-auto py-4">
      <div className="bg-ui-surface rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-ui-surface border-b border-ui-line px-4 sm:px-6 py-4 z-10">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-ui-amber-bg rounded-full flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-ui-amber-fg" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-ui-ink">Report Parsing Issue</h2>
                <p className="text-sm text-ui-muted">Help us improve document parsing</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-ui-muted hover:text-ui-ink-3 p-1"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-4 sm:p-6 space-y-6">
          {error && (
            <div className="bg-ui-red-bg border border-ui-red-line text-ui-red-fg px-4 py-3 rounded-md text-sm">
              {error}
            </div>
          )}

          {/* Component info */}
          <div className="bg-ui-sunk rounded-lg p-3">
            <p className="text-sm text-ui-ink-3">
              <span className="font-medium">Component:</span>{' '}
              {component.title || componentType.replace(/_/g, ' ')}
            </p>
            {tripId && (
              <p className="text-sm text-ui-muted mt-1">
                <span className="font-medium">Trip:</span> {component.trip_title || tripId}
              </p>
            )}
          </div>

          {/* Error Type Selection */}
          <div>
            <label className="block text-sm font-medium text-ui-ink-2 mb-2">
              What type of error is this? <span className="text-ui-red-fg">*</span>
            </label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {ERROR_CATEGORIES.map(category => (
                <label
                  key={category.value}
                  className={`flex items-start p-3 rounded-lg cursor-pointer border-2 transition-colors ${
                    selectedCategories.includes(category.value)
                      ? 'border-ui-amber-solid bg-ui-amber-bg'
                      : 'border-ui-line hover:border-ui-control'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={selectedCategories.includes(category.value)}
                    onChange={() => toggleCategory(category.value)}
                    className="h-4 w-4 text-ui-amber-fg border-ui-control rounded mt-0.5"
                  />
                  <div className="ml-3">
                    <span className="text-sm font-medium text-ui-ink">{category.label}</span>
                    <p className="text-xs text-ui-muted">{category.description}</p>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {/* Affected Fields */}
          <div>
            <label className="block text-sm font-medium text-ui-ink-2 mb-2">
              Which fields are incorrect? (optional)
            </label>
            <div className="flex flex-wrap gap-2">
              {fieldOptions.map(field => (
                <button
                  key={field}
                  type="button"
                  onClick={() => toggleField(field)}
                  className={`px-3 py-1.5 rounded-full text-sm transition-colors ${
                    selectedFields.includes(field)
                      ? 'bg-ui-amber-strong text-ui-on-primary'
                      : 'bg-ui-hover text-ui-ink-2 hover:bg-ui-line'
                  }`}
                >
                  {field.replace(/_/g, ' ')}
                </button>
              ))}
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-ui-ink-2 mb-2">
              Describe the issue <span className="text-ui-red-fg">*</span>
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What was wrong with the parsed data? What should it have been?"
              rows={3}
              className="w-full border border-ui-control rounded-md px-3 py-2 text-sm focus:ring-ui-amber-solid focus:border-ui-amber-solid"
            />
          </div>

          {/* Expected Values */}
          <div>
            <label className="block text-sm font-medium text-ui-ink-2 mb-2">
              Correct values (optional)
            </label>
            <textarea
              value={expectedValues}
              onChange={(e) => setExpectedValues(e.target.value)}
              placeholder="Enter the correct values, e.g.:\nDeparture time: 9:00 AM\nArrival time: 5:30 PM"
              rows={3}
              className="w-full border border-ui-control rounded-md px-3 py-2 text-sm focus:ring-ui-amber-solid focus:border-ui-amber-solid font-mono"
            />
            <p className="text-xs text-ui-muted mt-1">
              Provide the values that should have been extracted
            </p>
          </div>

          {/* Priority */}
          <div>
            <label className="block text-sm font-medium text-ui-ink-2 mb-2">
              Priority
            </label>
            <div className="flex flex-wrap gap-2">
              {PRIORITY_OPTIONS.map(option => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => setPriority(option.value)}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                    priority === option.value
                      ? option.color + ' ring-2 ring-offset-1 ring-ui-control'
                      : 'bg-ui-hover text-ui-ink-3 hover:bg-ui-line'
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          {/* Actions */}
          <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="w-full sm:w-auto px-4 py-2 text-ui-ink-2 bg-ui-surface border border-ui-control rounded-md hover:bg-ui-hover"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="w-full sm:w-auto px-6 py-2 bg-ui-amber-strong text-ui-on-primary rounded-md hover:bg-ui-amber-strong/90 disabled:opacity-50 font-medium"
              disabled={loading}
            >
              {loading ? 'Submitting...' : 'Submit Feedback'}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
};

export default ParsingFeedbackModal;
